import { initGrowthBookConfig } from '@doxyme/config';
import { getQueryLocale } from '@doxyme/locale';

import { listenForFileTransfer } from '../../components/call/fileTransferDialog/listenForFileTransferEvents';
import { initializeAsync as initializeBranding } from '../../features/branding/branding.slice';
import {
  getInstitutionAsync,
  initialize as initializeInstitution,
} from '../../features/institution/institution.slice';
import { initialize as initializeRealtime } from '../../features/realtime/realtime.slice';
import { getIsDataDogRumEnabled } from '../../features/tracking/trackers/datadog/config.datadog';
import {
  type GetLocale,
  initDataDogRum,
} from '../../features/tracking/trackers/datadog/rum.datadog';
import { type GetSessionId } from '../../features/tracking/trackers/datadog/beforeSendRum.datadog';
import { loadDokbotLibrary } from '../../features/tracking/trackers/dokbot';
import { disableAnimationIfNeeded } from '../../features/uiState/thunks';
import { listenForChatMessages } from '../../hooks/useChat/initChat';
import { businessServicesConfig } from '../../services/businessServicesConfig';
import { getGPUData } from '../../shared/deviceCapabilities/getGpuData';
import { _getGrowthBookInstance } from '../../shared/growthBook/getGrowthBookInstance';
import { store } from '../../stores/redux';
import { overwriteSetSinkId } from '../setSinkId/overwrite.setSinkId';
import { getIssueDetector } from '../../services/call/qos/utils/issueDetector';
import { initializeFronteggPage } from './fronteggPage.init';
import '../../features/observability/lcpReporting';

const getLocale: GetLocale = (): string => {
  const locale: string = getQueryLocale() ?? navigator.language;
  return locale;
};

const isFronteggAuthPage = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/v2/auth/account');
};

/**
 * Initializes tooling for Client-Side. That are common for both patient and provider.
 *
 * @param _getIsDataDogRumEnabled - Internal parameter for Dependency Injection [DO NOT USE] -
 * Function that returns whether DataDog RUM is enabled
 * @param _initDataDogRum - Internal parameter for Dependency Injection [DO NOT USE] -
 * Function that initializes DataDog RUM
 * @returns {Promise<void>}
 */
export const initializeClient = async (
  _getIsDataDogRumEnabled: typeof getIsDataDogRumEnabled = getIsDataDogRumEnabled,
  _initDataDogRum: typeof initDataDogRum = initDataDogRum,
): Promise<void> => {
  // Initialize issue detector. It should be done before any other initialization due to monkey patching of RTCPeerConnection.
  getIssueDetector();

  if (isFronteggAuthPage()) {
    initializeFronteggPage();
  }

  overwriteSetSinkId();
  if (_getIsDataDogRumEnabled()) {
    const getSessionId: GetSessionId = () =>
      store.getState().apolloSession.sessionId;
    _initDataDogRum(getSessionId, getLocale);
  }
  // get token is for providers only, but without this patient services won't work
  businessServicesConfig.configure({
    getToken: () => undefined, // Will be updated by useUpdateApiHeaders hook
  });

  loadDokbotLibrary();

  // We try to initialize Growthbook with user data, but it is still available
  // for patients without a userId, or on pages where the provider is not logged in yet.
  // Without a user id, only the global default flags are returned, not the use specific ones.
  await initGrowthBookConfig(await _getGrowthBookInstance());

  listenForChatMessages();
  listenForFileTransfer();

  void store.dispatch(initializeRealtime());

  const institution = await store.dispatch(getInstitutionAsync()).unwrap();

  store.dispatch(initializeInstitution({ institution }));

  await store.dispatch(initializeBranding(institution?.id?.toString() ?? ''));

  void getGPUData();

  void store.dispatch(disableAnimationIfNeeded());
};
